<template>
  <div
    id="app"
    class="v-search"
    :data-mobile="mobileView"
  >
    <FiltersAboveMapExperiment />
    <div v-if="$store.getters.experimentChipsFiltersVisible || $store.state.experimentChipsFiltersWasMounted" class="v-search-page-filters">
      <SearchFilters />
    </div>
    <SearchRooms class="v-search-rooms-component" :class="{'v-search-rooms-component--tm4728': $store.getters.experimentChipsFiltersVisible}" />
    <SearchMap class="v-search-map-component" :class="{'v-search-map-component--tm4728': $store.getters.experimentChipsFiltersVisible}" />
    <MobileControls v-if="mobileView !== 'map'" />

    <WishlistModals />
  </div>
</template>

<script>
import debounce from 'debounce';
import { mapGetters, mapState } from 'vuex';

import FiltersAboveMapExperiment from '@/modules/public/search/components/FiltersAboveMapExperiment/FiltersAboveMapExperiment.vue';
import { clearOldSearchInteractionsEntries, trackSearchVisit } from '@/services/search-interactions-tracker';
import { captureUtmParams } from '@/services/utm-tracker';

import WishlistModals from '../enquiry-form/components/WishlistModals.vue';
import MobileControls from './components/MobileControls.vue';
import SearchFilters from './components/SearchFilters/SearchFilters.vue';
import SearchMap from './components/SearchMap/SearchMap.vue';
import SearchRooms from './components/SearchRooms.vue';
import { visibilityTracker } from './data-tracking/search-visibility-tracker.js';
import { isMobile as isMobileFn } from './services.js';

export default {
  name: 'App',
  components: {
    WishlistModals,
    MobileControls,
    SearchRooms,
    SearchMap,
    SearchFilters,
    FiltersAboveMapExperiment,
  },

  computed: {
    ...mapState([
      'eventTypeSlug',
      'isMobile',
      'perPage',
      'mobileView',
      'totalRooms',
      'hashId',
      'rooms',
    ]),
    ...mapGetters([
      'appliedFilters',
    ]),
  },
  watch: {
    // enable when people are set dynamically
    '$store.state.filters.people.value': {
      handler(value) {
        if (value > 0 && window.SEARCH_ALGO_EXPERIMENT_ID && window.SEARCH_ALGO_VARIATION !== null) {
          window.dataLayer.push({
            event: 'experiment_impression',
            experiment_id: window.SEARCH_ALGO_EXPERIMENT_ID,
            variant_id: window.SEARCH_ALGO_VARIATION,
          });

          console.info(`In s experiment, variation: ${window.SEARCH_ALGO_VARIATION}`);
        }
      },
      once: true,
    },
  },
  methods: {
    onResize() {
      this.$store.commit('setMobile', isMobileFn());
      if (!isMobileFn()) {
        $.publish('v.search.init');
      }
    },
  },
  mounted() {
    if (!isMobileFn() || this.mobileView === 'map') {
      $.publish('v.search.init');
    }

    this.$store.dispatch('updateTrackingCity');

    setTimeout(() => {
      // initial state contained only subset of rooms for fastest first paint
      // lazy load the rest in a way it's not noticeable by the user
      this.$store.commit('setDisplayedRooms', this.perPage);
      this.$store.dispatch('observeVisibleRoomsAsync');
      this.$store.dispatch('initTimeTrackingWatchEvents');
    }, this.isMobile ? 1500 : 700);

    window.addEventListener('resize', debounce(this.onResize, 200), false);
    window.addEventListener('beforeunload', () => {
      this.$store.dispatch('trackVisibleRooms');
    });

    this.$nextTick(() => {
      App.es6.shared.performanceTracker.addPerformanceMark('VUE_READY');
    });

    App.track.all({
      category: 's-search-page',
      action: 'initial-rooms-number',
      context: this.eventTypeSlug,
      numeric_value: this.totalRooms,
      value: this.totalRooms,
      label: this.hashId,
      meta: JSON.stringify(this.appliedFilters),
    });

    this.$store.dispatch('setTotalResultsVariable', this.rooms.length);
    this.$store.dispatch('setRoomTagCookie');
    this.$store.dispatch('setFiltersCookie');
    this.$store.dispatch('listenToUrlHistoryChange');
    this.$store.dispatch('trackGA4PageView');

    // add extra saving on changing tabs without clicking rooms or closing tabs
    document.addEventListener('visibilitychange', () => {
      const seen = visibilityTracker.getDisplayedRooms();
      const clicked = visibilityTracker.getClickedRooms();
      trackSearchVisit(seen, clicked);
    });

    clearOldSearchInteractionsEntries();
    captureUtmParams();
  },
};
</script>

<style lang="scss">
  @use "@sass/base/variables";
  @use "@sass/design-system/base-header";

  $filter-bar: 56px;

  .v-search-rooms-component, .v-search-rooms-width {
    width: 60%;
    @media (max-width: variables.$screen-im-max) {
      width: 100%;
    }
  }
  .v-search-rooms-component {
    margin-top: base-header.$header-height;
    @media #{variables.$for-mobile} {
      margin-top: base-header.$header-height-mobile;
    }
  }

  .v-search-map-component {
    width: 40%;
    height: calc(100vh - base-header.$header-height);
    background: #eee;
    position: fixed;
    overflow: hidden;
    top: base-header.$header-height;
    right: 0;
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
    @media (max-width: variables.$screen-im-max) {
      width: 100%;
      height: calc(100vh - base-header.$header-height-mobile); // fallback
      height: calc(100svh - base-header.$header-height-mobile);
      top: base-header.$header-height-mobile;
    }
  }

  #app {
    margin-top: base-header.$header-height;
    padding-top: $filter-bar;

    @media #{variables.$for-mobile} {
      margin-top: base-header.$header-height-mobile;
    }
  }

  @media (max-width: variables.$screen-im-max) {
    .v-search-filters-a-test, .v-search-page-results {
      opacity: 0;
      height: 0;
      overflow: hidden;
    }

    .v-search[data-mobile="filters"] .v-search-filters-a-test,
    .v-search[data-mobile="results"] .v-search-page-results,
    .v-search[data-mobile="map"] .v-search-page-map {
      display: block;
    }

    .v-search[data-mobile="filters"] .v-search-filters-a-test,
    .v-search[data-mobile="results"] .v-search-page-results {
      opacity: 1;
      height: inherit;
      overflow: inherit;
    }

    .v-search:not([data-mobile="map"]) .v-search-page-map {
      z-index: -1;
      opacity: 0;
      pointer-events: none;
    }
  }
</style>
